import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { BagButton } from './BagButton';
import { primaryNav } from '../../data/navigation';
import { useAnchorNavigation } from '../../hooks/useAnchorNavigation';

/** Shared header. Navigation later maps to the native iNavMenu. */
export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const goTo = useAnchorNavigation();
  const reduceMotion = useReducedMotion();
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const linkClass = 'inline-flex min-h-[44px] items-center text-[14px] font-medium text-ink transition-colors duration-150 ease-out hover:text-bronze-deep';
  return <header className="sticky top-[var(--prototype-top-offset,0px)] z-40 md:top-0 border-b border-line bg-canvas" data-id="element-319" data-mp="3t2m30h72llnc">
      <div className="mx-auto flex h-[88px] max-w-page items-center justify-between gap-4 px-5 md:h-[104px] md:px-10" data-id="element-320" data-mp="3o39fdu8dkajp">
        <Link to="/" aria-label="AuraGlow Luxe home" className="block shrink-0" data-id="element-321" data-mp="kl95uan67x3z">
          <BrandLogo className="w-44 md:w-56" data-id="element-322" data-mp="flms803fkeun" />
        </Link>

        <nav aria-label="Primary" className="hidden md:block" data-id="element-323" data-mp="fm6bbk4th9a9">
          <ul className="flex items-center gap-5 lg:gap-9" data-id="element-324" data-mp="au4s1e98eiaq">
            {primaryNav.map(item => <li key={item.label} data-id="element-325" data-mp="6u4d7si80535">
                {item.anchor ? <a href={`/#${item.anchor}`} onClick={goTo(item.anchor)} className={linkClass} data-id="element-326" data-mp="13b7fgv6m9y9q">
                    {item.label}
                  </a> : <NavLink to={item.to ?? '/'} className={({
              isActive
            }) => `${linkClass} ${isActive ? 'underline decoration-bronze underline-offset-[6px]' : ''}`} data-id="element-327" data-mp="yie9y53bqlwl">
                    {item.label}
                  </NavLink>}
              </li>)}
          </ul>
        </nav>

        <div className="flex items-center gap-1" data-id="element-328" data-mp="2nz9s5tsc4pl5">
          <BagButton data-id="element-329" data-mp="2a0tlu3ydx13j" />
          <button type="button" className="inline-flex h-11 w-11 items-center justify-center rounded-[3px] text-ink md:hidden" aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? 'Close menu' : 'Open menu'} onClick={() => setOpen(v => !v)} data-id="element-330" data-mp="2a0a5ussaoms5">
            {open ? <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-331" data-mp="3uhybegw7oxy0" /> : <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-332" data-mp="3uihrrt67re9x" />}
          </button>
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-333" data-mp="3alzaga6raoy">
        {open && <motion.nav id="mobile-menu" aria-label="Mobile" initial={reduceMotion ? false : {
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute inset-x-0 top-full border-b border-line bg-canvas md:hidden" data-id="element-334" data-mp="1nuhfc55gwvro">
            <ul className="mx-auto max-w-page divide-y divide-line px-5" data-id="element-335" data-mp="26m0g05c0rxqj">
              {primaryNav.map(item => <li key={item.label} data-id="element-336" data-mp="2gi3gl6r83obd">
                  {item.anchor ? <a href={`/#${item.anchor}`} onClick={goTo(item.anchor, () => setOpen(false))} className="flex min-h-[52px] items-center font-display text-2xl text-ink" data-id="element-337" data-mp="2gg0u2wpa4dbe">
                      {item.label}
                    </a> : <Link to={item.to ?? '/'} className="flex min-h-[52px] items-center font-display text-2xl text-ink" data-id="element-338" data-mp="imob81kqxo24">
                      {item.label}
                    </Link>}
                </li>)}
            </ul>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}